import React, { useState } from 'react';
import {
  Card,
  Table,
  Form,
  Input,
  Button,
  Tag,
  Image,
  Space,
  Select,
  Modal,
  InputNumber,
  message,
} from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTable } from '@/hooks/useTable';
import {
  getArtistSpaceList,
  updateArtistSpace,
} from '@/api/artistSpace';
import type { ArtistSpaceListParams, ArtistSpaceTopic } from '@/api/artistSpace';
import { PageLink } from '@/config/pageConfig';
import dayjs from 'dayjs';
import { FORMAT_TIME } from '@/types/common';

const STATUS_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '已下架', color: 'default' },
  1: { text: '上架中', color: 'success' },
};

const CommunityTopicMngPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useState<ArtistSpaceListParams>({
    currentPage: 1,
    pageSize: 10,
  });
  const [takedownModal, setTakedownModal] = useState<{
    visible: boolean;
    record: ArtistSpaceTopic | null;
  }>({ visible: false, record: null });
  // const [sortModal, setSortModal] = useState<{
  //   visible: boolean;
  //   record: ArtistSpaceTopic | null;
  //   sort: number;
  // }>({ visible: false, record: null, sort: 0 });

  const {
    data,
    isLoading,
    handleTableChange,
    updateParams,
    currentPage,
    pageSize,
    total,
    refresh,
    reload,
    setCurrentPage,
    setPageSize,
  } = useTable<ArtistSpaceTopic, ArtistSpaceListParams>({
    queryKey: 'community-topic-list',
    queryFn: async (current, size, params) => {
      const res = await getArtistSpaceList({
        ...params,
        currentPage: current,
        pageSize: size,
      });
      return {
        total: res.total ?? 0,
        currentPage: res.currentPage ?? current,
        pageSize: res.pageSize ?? size,
        records: res.records ?? [],
      };
    },
    defaultParams: searchParams,
  });

  // 从投稿作品页返回时恢复页码
  React.useEffect(() => {
    const state = location.state as {
      refreshList?: boolean;
      returnPage?: number;
      returnPageSize?: number;
    } | null;
    if (state?.refreshList) {
      if (state.returnPage != null) setCurrentPage(state.returnPage);
      if (state.returnPageSize != null) setPageSize(state.returnPageSize);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const handleSearch = async () => {
    const values = await form.validateFields().catch(() => ({}));
    const next = {
      ...searchParams,
      ...values,
      currentPage: 1,
      pageSize,
    };
    setSearchParams(next);
    updateParams(next);
  };

  const handleReset = () => {
    form.resetFields();
    const next = { currentPage: 1, pageSize: 10 };
    setSearchParams(next);
    updateParams(next);
  };

  const handleViewSubmissions = (record: ArtistSpaceTopic) => {
    const topicId = record.artistSpaceId;
    if (topicId == null) return;
    navigate(PageLink.CommunityTopicSubmissions.replace(':id', String(topicId)), {
      state: { topicTitle: record.name, fromListPage: currentPage, fromListPageSize: pageSize },
    });
  };

  const handleTakedownClick = (record: ArtistSpaceTopic) => {
    setTakedownModal({ visible: true, record });
  };

  const handleTakedownConfirm = async () => {
    const record = takedownModal.record;
    if (!record?.artistSpaceId) return;
    try {
      await updateArtistSpace({
        artistSpaceId: record.artistSpaceId,
        status: 0,
      });
      message.success('下架成功');
      setTakedownModal({ visible: false, record: null });
      reload();
    } catch (e) {
      message.error((e as Error).message || '下架失败');
    }
  };

  const handleRelistClick = async (record: ArtistSpaceTopic) => {
    if (!record?.artistSpaceId) return;
    try {
      await updateArtistSpace({
        artistSpaceId: record.artistSpaceId,
        status: 1,
      });
      message.success('重新上架成功');
      reload();
    } catch (e) {
      message.error((e as Error).message || '上架失败');
    }
  };

  // const handleSortClick = (record: ArtistSpaceTopic, index: number) => {
  //   setSortModal({
  //     visible: true,
  //     record,
  //     sort: record.sort,
  //   });
  // };

  // const handleSortSave = async () => {
  //   const { record, sort } = sortModal;
  //   if (!record?.artistSpaceId) return;
  //   try {
  //     await updateArtistSpace({
  //       artistSpaceId: record.artistSpaceId,
  //       sort,
  //     });
  //     message.success('保存成功');
  //     setSortModal({ visible: false, record: null, sort: 0 });
  //     refresh();
  //   } catch (e) {
  //     message.error((e as Error).message || '保存失败');
  //   }
  // };

  const columns = [
    {
      title: 'id',
      dataIndex: 'artistSpaceId',
      key: 'artistSpaceId',
      width: 50,
      render: (v: number | undefined | null, record: ArtistSpaceTopic) =>
        record.artistSpaceId != null ? record.artistSpaceId : '-',
    },
    {
      title: '主题',
      dataIndex: 'name',
      key: 'name',
      width: 140,
      ellipsis: true,
      render: (v: string) => v || '-',
    },
    {
      title: '封面',
      dataIndex: 'cover',
      key: 'cover',
      width: 80,
      render: (url: string) =>
        url ? (
          <Image
            src={url}
            alt="封面"
            style={{ width: 56, height: 56, objectFit: 'cover' }}
          />
        ) : (
          <span style={{ color: '#999' }}>暂无</span>
        ),
    },
    {
      title: '发起者昵称',
      dataIndex: 'promoterName',
      key: 'promoterName',
      width: 120,
      ellipsis: true,
      render: (v: string) => v || '-',
    },
    {
      title: 'SUPP ID',
      dataIndex: 'userNumber',
      key: 'userNumber',
      width: 100,
      render: (v: string) => v || '-',
    },
    {
      title: '参与人数',
      dataIndex: 'participantsNumber',
      key: 'participantsNumber',
      width: 80,
      render: (v: number) => v ?? '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 96,
      render: (status: number) => (
        <Tag color={STATUS_MAP[status]?.color ?? 'default'}>
          {STATUS_MAP[status]?.text ?? '未知'}
        </Tag>
      ),
    },
    {
      title: '发起时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 172,
      render: (v: string) => (v ? dayjs(v).format(FORMAT_TIME) : '-'),
    },
    {
      title: '操作',
      key: 'action',
      // width: 248,
      width: 180,
      fixed: 'right' as const,
      render: (_: unknown, record: ArtistSpaceTopic, index: number) => (
        <Space size="small">
          <Button
            type="link"
            onClick={() => handleViewSubmissions(record)}
          >
            查看投稿作品
          </Button>
          {/* <Button type="link" onClick={() => handleSortClick(record, index)}>
            排序
          </Button> */}
          {record.status === 1 ? (
            <Button
              type="link"
              danger
              onClick={() => handleTakedownClick(record)}
            >
              违规下架
            </Button>
          ) : (
            <Button
              type="link"
              onClick={() => handleRelistClick(record)}
            >
              重新上架
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card title="话题管理">
        <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="userNumber" label="用户ID">
            <Input placeholder="请输入用户SUPP ID" style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="status" label="话题状态">
            <Select
              placeholder="全部"
              allowClear
              style={{ width: 120 }}
              options={[
                { label: '全部', value: undefined },
                { label: '上架中', value: 1 },
                { label: '已下架', value: 0 },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={handleSearch} style={{ marginRight: 8 }}>
              查询
            </Button>
            <Button onClick={handleReset}>
              重置
            </Button>
          </Form.Item>
        </Form>

        <Table<ArtistSpaceTopic>
          rowKey="artistSpaceId"
          columns={columns}
          dataSource={data}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
        />
      </Card>

      <Modal
        title="违规下架"
        open={takedownModal.visible}
        onCancel={() => setTakedownModal({ visible: false, record: null })}
        onOk={handleTakedownConfirm}
        okText="确定"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <p>确定下架「{takedownModal.record?.name}」话题吗？</p>
        <p style={{ color: '#666', fontSize: 12 }}>
          下架后用户在客户端无法浏览该话题，管理员可重新上架。
        </p>
      </Modal>

      {/* <Modal
        title="话题排序"
        open={sortModal.visible}
        onCancel={() =>
          setSortModal({ visible: false, record: null, sort: 0 })
        }
        onOk={handleSortSave}
        okText="保存"
        cancelText="取消"
      >
        <p style={{ marginBottom: 8 }}>当前话题: {sortModal.record?.name}</p>
        <Form.Item label="排序序号" required>
          <InputNumber
            min={0}
            value={sortModal.sort}
            onChange={(v) =>
              setSortModal((prev) => ({ ...prev, sort: v ?? 0 }))
            }
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Modal> */}
    </>
  );
};

export default CommunityTopicMngPage;
